import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { LenderOpportunitiesApi } from '../tokens/lender-opportunities.token';
import {
  ActiveAuctionsEnvelope,
  LenderActiveAuction,
  LenderOpportunity,
  MarketplaceFeed,
  OpportunitiesEnvelope,
} from '../models/lender-origination.model';
import { LenderReportJob } from '../models/financing-lender-report.model';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-only implementation of {@link LenderOpportunitiesApi} (EM-699 / G2 — PRD §5.7 + §5.11).
 *
 * Issues the real calls to the two lender marketplace feeds implemented by
 * `LenderOpportunitiesResource`:
 *   - `GET /v1/lender/opportunities` — Stage-16A auctions open for the lender to bid on;
 *   - `GET /v1/lender/auctions` — every live auction (16A→16C) the lender is involved in, i.e. also the
 *     ones it has already bid on, so they can be revised / negotiated from the Active Auctions board.
 * Both are FACTORING and WC only (SDF excluded server-side per BR-LEN-002).
 *
 * EM-1635 — a failed call never collapses into a silent empty list. It resolves to
 * `{ available: false, errorMessage }` carrying the backend's own RFC-7807 message (Rule 09), so the
 * screen can say "the feed could not be loaded" instead of the indistinguishable "nothing matched your
 * appetite". Those two states led a tester to conclude the screen never called the endpoint at all;
 * they must never render the same. The mapping lives in ONE place ({@link toFeed}) so both feeds keep
 * that distinction (Rule 17).
 *
 * Rule 07 / Rule 10 — REST only, no Mock class. Playwright intercepts via `page.route()`.
 */
@Injectable()
export class LenderOpportunitiesRestService implements LenderOpportunitiesApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.LENDER_OPPORTUNITIES;
  private readonly auctionsBase = API_PATHS.LENDER_ACTIVE_AUCTIONS;

  list(): Observable<OpportunitiesEnvelope> {
    return this.toFeed(this.http.get<LenderOpportunity[]>(this.base), 'lenderSm.opportunities.loadFailed');
  }

  listActiveAuctions(): Observable<ActiveAuctionsEnvelope> {
    return this.toFeed(
      this.http.get<LenderActiveAuction[]>(this.auctionsBase),
      'lenderSm.activeAuctions.loadFailed',
    );
  }

  /**
   * EM-1782 — the deal's BRS §4.6.3 Risk Assessment Package. Queue → poll → download, against the
   * EXISTING `LenderReportService` job pipeline (Rule 17 — the lender's Risk Package IS the Stage-15
   * deal report, not a second one). Errors propagate raw for Rule-09 surfacing.
   */
  requestRiskPackage(financingRequestId: number, format?: string): Observable<LenderReportJob> {
    const query = format ? `?format=${encodeURIComponent(format)}` : '';
    return this.http.post<LenderReportJob>(`${this.base}/${financingRequestId}/risk-package${query}`, {});
  }

  getRiskPackageJob(financingRequestId: number, jobId: number): Observable<LenderReportJob> {
    return this.http.get<LenderReportJob>(`${this.base}/${financingRequestId}/risk-package/${jobId}`);
  }

  downloadRiskPackage(financingRequestId: number, jobId: number): Observable<Blob> {
    return this.http.get(`${this.base}/${financingRequestId}/risk-package/${jobId}/download`, {
      responseType: 'blob',
    });
  }

  /**
   * Wraps a feed request in the availability envelope: a 2xx becomes `{ available: true, items }`,
   * and ANY failure becomes `{ available: false, items: [], errorMessage }` with the backend's own
   * message (Rule 09) — never an empty list that reads as "nothing matched".
   */
  private toFeed<T>(request: Observable<T[]>, errorCode: string): Observable<MarketplaceFeed<T>> {
    return request.pipe(
      map((items): MarketplaceFeed<T> => ({ available: true, items: items ?? [] })),
      catchError((err: HttpErrorResponse) =>
        of<MarketplaceFeed<T>>({
          available: false,
          items: [],
          errorCode,
          errorMessage: extractBackendErrorMessage(err, ''),
        }),
      ),
    );
  }
}
